import { Component, OnInit } from '@angular/core';
import { ChartOptions, ChartType, ChartDataSets } from 'chart.js';
import { Label } from 'ng2-charts';
import { DatabaseService } from 'src/_services/DatabaseService';
import { sessionStorage } from 'src/app/localstorage.service';
import { ToastrManager } from 'ng6-toastr-notifications';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

  // ── Theme palette ─────────────────────────────────────────
  private readonly C = {
    navy: '#0f1f45', gold: '#ecc31f', indigo: '#6366f1', violet: '#8b5cf6',
    green: '#22c55e', amber: '#f59e0b', red: '#ef4444', blue: '#0ea5e9', slate: '#64748b',
  };

  login_data: any = {};

  // =====================================================================
  //  HR RECRUITMENT TAB
  // =====================================================================
  hrLoader = false;
  // Pipeline stages in order (must match Hr_Recruitment tabCount keys)
  hrStages: string[] = ['Pending', 'Screening', 'Suniti Round', 'GB Round', 'Manager Round',
    'Under Process', 'Final Select', 'Under Negotiation', 'Letter Sent', 'Joined'];

  hrKpi = { all: 0, pipeline: 0, joined: 0, rejected: 0, withdrawal: 0, hold: 0, joinRate: 0 };

  // Recruitment funnel (bar)
  hrFunnelLabels: Label[] = [];
  hrFunnelData: ChartDataSets[] = [];
  hrFunnelColors: any[] = [{ backgroundColor: '#0f1f45', hoverBackgroundColor: '#ecc31f' }];
  barOptions: ChartOptions = this.baseBarOptions();

  // Candidate status split (doughnut)
  hrStatusLabels: Label[] = ['Pending', 'Pass', 'Fail'];
  hrStatusData: number[] = [0, 0, 0];
  hrStatusColors: any[] = [{ backgroundColor: [this.C.amber, this.C.green, this.C.red] }];
  doughnutType: ChartType = 'doughnut';
  doughnutOptions: ChartOptions = this.baseDoughnutOptions();

  // Candidates by employee type (bar)
  hrEmpTypeLabels: Label[] = ['Sales', 'Admin', 'Plant'];
  hrEmpTypeData: ChartDataSets[] = [{ data: [0, 0, 0], label: 'Candidates' }];
  hrEmpTypeColors: any[] = [{ backgroundColor: [this.C.indigo, this.C.blue, this.C.violet] }];

  // Top native places (computed from candidate rows)
  hrTopLocations: { name: string, count: number, pct: number }[] = [];

  // =====================================================================
  //  INVENTORY TAB
  // =====================================================================
  invLoader = false;
  invFilter: any = {};
  invDepartments: any[] = [];
  invKpi: any = { total: 0, low: 0, over: 0, out: 0, ok: 0 };
  invLowList: any[] = [];
  invOverList: any[] = [];
  invDeptAgg: any[] = [];
  returnsDue: any[] = [];
  returnsOverdue = 0;
  returnsDueSoon = 0;

  // Stock health (doughnut)
  invHealthLabels: Label[] = ['Healthy', 'Low', 'Over', 'Out'];
  invHealthData: number[] = [0, 0, 0, 0];
  invHealthColors: any[] = [{ backgroundColor: [this.C.green, this.C.amber, this.C.indigo, this.C.red] }];

  // Stock status by department (stacked bar)
  invDeptLabels: Label[] = [];
  invDeptData: ChartDataSets[] = [];
  invDeptColors: any[] = [
    { backgroundColor: this.C.green }, { backgroundColor: this.C.amber },
    { backgroundColor: this.C.indigo }, { backgroundColor: this.C.red },
  ];
  stackedBarOptions: ChartOptions = this.baseStackedOptions();
  hBarType: ChartType = 'horizontalBar';
  hStackedOptions: ChartOptions = this.baseHStackedOptions();

  constructor(public serve: DatabaseService, public session: sessionStorage, public toast: ToastrManager) { }

  ngOnInit() {
    const s: any = this.session.getSession();
    this.login_data = s && s.value ? s.value.data : {};
    this.loadHr();
    this.loadInvDepartments();
    this.loadInventory();
    this.loadReturnsDue();
  }

  // ── HR loaders ────────────────────────────────────────────
  loadHr() {
    this.hrLoader = true;
    this.serve.post_rqst({}, 'Hr_Recruitment/getCandidates').subscribe((r: any) => {
      this.hrLoader = false;
      if (r['statusCode'] != 200) { this.toast.errorToastr(r['statusMsg'] || 'HR data load failed'); return; }
      const tabCount = r['tabCount'] || {};
      const subTabCount = r['subTabCount'] || {};
      const rows = r['result'] || [];
      this.buildHrKpi(tabCount);
      this.buildHrFunnel(tabCount);
      this.buildHrEmpType(subTabCount);
      this.buildHrStatus(rows);
      this.buildHrLocations(rows);
    }, () => { this.hrLoader = false; this.toast.errorToastr('HR data load failed'); });
  }

  private buildHrKpi(tc: any) {
    const n = (k: string) => Number(tc[k] || 0);
    const all = n('All');
    const joined = n('Joined');
    const rejected = n('Rejected');
    const withdrawal = n('Withdrawal');
    const hold = n('Hold');
    const pipeline = Math.max(all - joined - rejected - withdrawal - hold, 0);
    // Offer -> Join rate: joined vs everyone who reached an offer-type stage
    const offered = joined + n('Letter Sent') + n('Under Negotiation');
    const joinRate = offered > 0 ? Math.round((joined / offered) * 100) : 0;
    this.hrKpi = { all, pipeline, joined, rejected, withdrawal, hold, joinRate };
  }

  private buildHrFunnel(tc: any) {
    this.hrFunnelLabels = this.hrStages.slice();
    this.hrFunnelData = [{ data: this.hrStages.map(s => Number(tc[s] || 0)), label: 'Candidates' }];
  }

  private buildHrEmpType(sub: any) {
    const totals: any = { Sales: 0, Admin: 0, Plant: 0 };
    // sum every stage bucket by employee type
    Object.keys(sub || {}).forEach(stage => {
      const b = sub[stage] || {};
      totals.Sales += Number(b['Sales'] || 0);
      totals.Admin += Number(b['Admin'] || 0);
      totals.Plant += Number(b['Plant'] || 0);
    });
    this.hrEmpTypeData = [{ data: [totals.Sales, totals.Admin, totals.Plant], label: 'Candidates' }];
  }

  private buildHrStatus(rows: any[]) {
    let pending = 0, pass = 0, fail = 0;
    rows.forEach(c => {
      const st = (c.current_status || '').toString().toLowerCase();
      if (st === 'pass') pass++;
      else if (st === 'fail') fail++;
      else pending++;
    });
    this.hrStatusData = [pending, pass, fail];
  }

  private buildHrLocations(rows: any[]) {
    const map: { [k: string]: number } = {};
    rows.forEach(c => {
      const p = (c.native_place || '').toString().trim();
      if (!p) return;
      map[p] = (map[p] || 0) + 1;
    });
    const arr = Object.keys(map).map(k => ({ name: k, count: map[k] }));
    arr.sort((a, b) => b.count - a.count);
    const max = arr.length ? arr[0].count : 1;
    this.hrTopLocations = arr.slice(0, 6).map(x => ({ ...x, pct: Math.round((x.count / max) * 100) }));
  }

  // ── Inventory loaders ─────────────────────────────────────
  loadInvDepartments() {
    this.serve.post_rqst({}, 'InventoryMgmt/categoryDropdown').subscribe((r: any) => {
      if (r['statusCode'] == 200) this.invDepartments = r['data'] || [];
    });
  }

  loadInventory() {
    this.invLoader = true;
    this.serve.post_rqst({ filter: this.invFilter }, 'InventoryMgmt/dashboard').subscribe((r: any) => {
      this.invLoader = false;
      if (r['statusCode'] != 200) { this.toast.errorToastr(r['statusMsg'] || 'Inventory data load failed'); return; }
      this.invKpi = r['kpi'] || { total: 0, low: 0, over: 0, out: 0, ok: 0 };
      this.invLowList = r['low_list'] || [];
      this.invOverList = r['over_list'] || [];
      this.invDeptAgg = r['dept_agg'] || [];
      this.buildInvHealth();
      this.buildInvDept();
    }, () => { this.invLoader = false; this.toast.errorToastr('Inventory data load failed'); });
  }

  loadReturnsDue() {
    this.serve.post_rqst({}, 'InventoryMgmt/gatePassDueList').subscribe((r: any) => {
      if (r['statusCode'] == 200) {
        this.returnsDue = r['data'] || [];
        this.returnsOverdue = Number(r['overdue'] || 0);
        this.returnsDueSoon = Number(r['due_soon'] || 0);
      }
    });
  }

  private buildInvHealth() {
    const k = this.invKpi || {};
    this.invHealthData = [Number(k.ok || 0), Number(k.low || 0), Number(k.over || 0), Number(k.out || 0)];
  }

  private buildInvDept() {
    const agg = this.invDeptAgg || [];
    this.invDeptLabels = agg.map(d => d.department && d.department !== '—' ? d.department : 'Unassigned');
    const ds = (data: number[], label: string): ChartDataSets =>
      ({ data, label, stack: 's', maxBarThickness: 30, barPercentage: 0.6, categoryPercentage: 0.7 } as ChartDataSets);
    this.invDeptData = [
      ds(agg.map(d => Number(d.ok || 0)), 'Healthy'),
      ds(agg.map(d => Number(d.low || 0)), 'Low'),
      ds(agg.map(d => Number(d.over || 0)), 'Over'),
      ds(agg.map(d => Number(d.out || 0)), 'Out'),
    ];
  }

  get invDeptChartHeight(): number {
    const n = (this.invDeptLabels || []).length || 1;
    return Math.min(Math.max(n * 52 + 80, 170), 460);
  }

  onDeptChange(id: any) {
    this.invFilter.department_id = id || 0;
    this.loadInventory();
  }

  refreshInv() { this.invFilter = {}; this.loadInventory(); this.loadReturnsDue(); }
  refreshHr() { this.loadHr(); }

  // ── Chart option builders ─────────────────────────────────
  private baseBarOptions(): ChartOptions {
    return {
      responsive: true, maintainAspectRatio: false,
      legend: { display: false },
      scales: {
        xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#64748b', fontSize: 10, autoSkip: false, maxRotation: 40, minRotation: 40 } }],
        yAxes: [{ ticks: { beginAtZero: true, fontColor: '#64748b' }, gridLines: { color: '#eef2f7' } }],
      },
    };
  }

  private baseStackedOptions(): ChartOptions {
    return {
      responsive: true, maintainAspectRatio: false,
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12, fontColor: '#475569' } },
      scales: {
        xAxes: [{ stacked: true, gridLines: { display: false }, ticks: { fontColor: '#64748b', fontSize: 10 } }],
        yAxes: [{ stacked: true, ticks: { beginAtZero: true, fontColor: '#64748b' }, gridLines: { color: '#eef2f7' } }],
      },
    };
  }

  private baseHStackedOptions(): ChartOptions {
    return {
      responsive: true, maintainAspectRatio: false,
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 14, fontColor: '#475569', usePointStyle: true } },
      tooltips: { mode: 'index', intersect: false },
      scales: {
        xAxes: [{ stacked: true, position: 'top', ticks: { beginAtZero: true, fontColor: '#94a3b8', fontSize: 11 }, gridLines: { color: '#eef2f7', zeroLineColor: '#e2e8f0' } }],
        yAxes: [{ stacked: true, gridLines: { display: false }, ticks: { fontColor: '#334155', fontSize: 12.5, fontStyle: '600' } }],
      },
    };
  }

  private baseDoughnutOptions(): ChartOptions {
    return {
      responsive: true, maintainAspectRatio: false, cutoutPercentage: 66,
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12, fontColor: '#475569' } },
    };
  }
}
